const FILENAME_FALLBACK = "conversation";
const UNSAFE_FILENAME_CHARS = /[\\/:*?"<>|\u0000-\u001f]/g;
// Excel cuts a cell off at 32,767 characters. Longer replies continue on the
// next row; the margin leaves room for the continuation marker.
const CSV_CELL_LIMIT = 32000;

export function toSafeFilename(title, maxLength = 60) {
  const cleaned = (title || "")
    .replace(UNSAFE_FILENAME_CHARS, "")
    .replace(/\s+/g, " ")
    .trim()
    // A trailing dot or space is legal in the string but not as a filename.
    .replace(/[. ]+$/, "");
  if (!cleaned) return FILENAME_FALLBACK;
  return cleaned.length > maxLength
    ? cleaned
        .slice(0, maxLength)
        .trimEnd()
        .replace(/[. ]+$/, "")
    : cleaned;
}

function parseTimestamp(ts) {
  if (ts == null || ts === "") return null;
  const date = new Date(ts);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatTimestamp(ts, language) {
  const date = parseTimestamp(ts);
  if (!date) return "";
  return date.toLocaleString(language === "zh" ? "zh-TW" : "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZoneName: "longOffset",
  });
}

export function formatDate(ts, language) {
  const date = parseTimestamp(ts);
  if (!date) return "";
  return date.toLocaleDateString(language === "zh" ? "zh-TW" : "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function formatTime(ts, language) {
  const date = parseTimestamp(ts);
  if (!date) return "";
  return date.toLocaleTimeString(language === "zh" ? "zh-TW" : "en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
}

const pad = (n) => String(n).padStart(2, "0");

// Local date and time in the forms Excel reads as a date and a time, so the
// columns sort and filter; "October 14, 2026" would stay plain text.
function sheetDate(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function sheetTime(date) {
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function utcOffset(date) {
  const minutes = -date.getTimezoneOffset();
  const abs = Math.abs(minutes);
  return `GMT${minutes < 0 ? "-" : "+"}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}

export function conversationTitle(conv, messages) {
  const firstUser = (messages || []).find((m) => m.role === "user");
  const raw = (
    conv?.title ||
    conv?.summary?.title ||
    firstUser?.message ||
    conv?.user_preview ||
    ""
  ).trim();
  if (!raw) return "";
  const firstLine = raw.split("\n")[0].trim();
  return firstLine || raw;
}

// A spreadsheet shows Markdown as literal symbols. Strips the bold, heading
// and code markup and our `table:` keyword, turns bullets into "•", and keeps
// the words, line breaks, links and pipe tables.
function toPlainText(text) {
  return (typeof text === "string" ? text : "")
    .replace(/\r\n?/g, "\n")
    .replace(/^[ \t]*```.*$\n?/gm, "")
    .replace(/^[ \t]*table:[ \t]*(?=\n|\||$)/gim, "")
    .replace(/^#{1,6}[ \t]+/gm, "")
    // Horizontal rules, before bullets so "- - -" is not read as one.
    .replace(/^[ \t]*([-*_])(?:[ \t]*\1){2,}[ \t]*$/gm, "")
    .replace(/^([ \t]*)[-*+][ \t]+/gm, "$1• ")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/`([^`\n]+)`/g, "$1")
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, (_, label, url) =>
      label === url ? url : `${label} (${url})`,
    )
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// Cuts at the last line break, or failing that the last space, in the back
// half of each piece, so a continuation row starts on a whole line or word.
function splitForCells(text, limit = CSV_CELL_LIMIT) {
  const pieces = [];
  let rest = text;
  while (rest.length > limit) {
    let cut = rest.lastIndexOf("\n", limit);
    if (cut < limit / 2) cut = rest.lastIndexOf(" ", limit);
    if (cut < limit / 2) {
      cut = limit;
      // Never between the two halves of an emoji or other surrogate pair.
      const code = rest.charCodeAt(cut - 1);
      if (code >= 0xd800 && code <= 0xdbff) cut -= 1;
    }
    pieces.push(rest.slice(0, cut).trimEnd());
    rest = rest.slice(cut).replace(/^\s+/, "");
  }
  if (rest || !pieces.length) pieces.push(rest);
  return pieces;
}

// Every cell is quoted, so commas, quotes and line breaks stay inside it. One
// that opens with = + - or @ would be run as a formula by Excel (an error at
// best); the apostrophe keeps it text.
function csvCell(value) {
  let text = String(value ?? "");
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
}

export function conversationToCsv(conv, messages, language) {
  const zh = language === "zh";
  const list = Array.isArray(messages) ? messages : [];
  const title =
    conversationTitle(conv, list) ||
    (zh ? "未命名對話" : "Untitled conversation");
  const created = parseTimestamp(conv?.created_at);
  const folder = conv?.folder?.trim();
  const summary = conv?.summary?.text?.trim();

  // The conversation's details, a blank row, then one row per message.
  const rows = [[zh ? "標題" : "Title", title]];
  if (created) {
    rows.push([
      zh ? "日期" : "Date",
      `${sheetDate(created)} ${sheetTime(created)} (${utcOffset(created)})`,
    ]);
  }
  if (folder) rows.push([zh ? "資料夾" : "Folder", folder]);
  rows.push([zh ? "訊息數" : "Messages", list.length]);
  if (summary) rows.push([zh ? "摘要" : "Summary", summary]);
  rows.push([]);
  rows.push(
    zh
      ? ["#", "日期", "時間", "發言者", "訊息"]
      : ["#", "Date", "Time", "Speaker", "Message"],
  );

  for (const [index, msg] of list.entries()) {
    const date = parseTimestamp(msg.timestamp);
    const who =
      msg.role === "user"
        ? zh
          ? "使用者"
          : "You"
        : zh
          ? "AI 代理人"
          : "AI Agent";
    const lead = [
      index + 1,
      date ? sheetDate(date) : "",
      date ? sheetTime(date) : "",
      who,
    ];
    // Each piece of a long reply repeats the number, time and speaker, so
    // sorting or filtering keeps its rows together.
    splitForCells(toPlainText(msg.message)).forEach((piece, part) => {
      rows.push([
        ...lead,
        part ? `${zh ? "（續）" : "(continued) "}${piece}` : piece,
      ]);
    });
  }

  // The BOM is what makes Excel read the file as UTF-8; without it Chinese
  // text opens garbled.
  return (
    "﻿" + rows.map((row) => row.map(csvCell).join(",")).join("\r\n")
  );
}

export function downloadTextFile(filename, text, mimeType = "text/csv") {
  const blob = new Blob([text], { type: `${mimeType};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  requestAnimationFrame(() => URL.revokeObjectURL(url));
}

export function exportConversationAsCsv(conv, messages, language) {
  const csv = conversationToCsv(conv, messages, language);
  const name = toSafeFilename(conversationTitle(conv, messages));
  downloadTextFile(`${name}.csv`, csv);
}

// Opens the print dialog over the print view, which must already be on the
// page. The tab title becomes the PDF's file name, and dark mode is lifted so
// the transcript prints dark on white; both come back when the dialog closes.
export function printConversation(conv, messages, onDone) {
  const root = document.documentElement;
  const previousTitle = document.title;
  const wasDark = root.classList.contains("dark");
  const restore = () => {
    window.removeEventListener("afterprint", restore);
    document.title = previousTitle;
    if (wasDark) root.classList.add("dark");
    onDone();
  };
  document.title = toSafeFilename(conversationTitle(conv, messages));
  root.classList.remove("dark");
  window.addEventListener("afterprint", restore);
  window.print();
}
